import { cn } from '@/shared/lib/utils';

export interface PageHeaderProps {
  title: string;
  /** The muted line under the title. */
  description?: React.ReactNode;
  /** Right-aligned, usually the "Create X" button. */
  actions?: React.ReactNode;
  /**
   * Rendered above the title, inside the same block — the create and edit pages
   * under /resources put their BackButton here. It has to be *inside* rather
   * than a sibling: the block has no internal gap, and the page's `space-y-6`
   * would push it away from the title it belongs to.
   */
  before?: React.ReactNode;
  /**
   * `start` when the action is a destructive button beside a title that may wrap
   * onto two lines — centring it against a two-line heading floats it oddly.
   * The /resources editors all use it.
   */
  actionsAlign?: 'center' | 'start';
  /**
   * Sits on the title's own line. The users page counts staff awaiting approval
   * there, where it reads as part of the heading rather than as a second fact.
   */
  titleAdornment?: React.ReactNode;
  className?: string;
}

/** A dashboard page's title block; the actions wrap under the title when both do not fit. */
export function PageHeader({
  title,
  description,
  actions,
  before,
  actionsAlign = 'center',
  titleAdornment,
  className,
}: PageHeaderProps) {
  const heading = (
    <div>
      {before}
      {titleAdornment ? (
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
          {titleAdornment}
        </div>
      ) : (
        <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
      )}
      {description ? <p className="text-muted-foreground">{description}</p> : null}
    </div>
  );

  if (!actions) {
    return <div className={className}>{heading}</div>;
  }

  return (
    <div
      className={cn(
        'flex flex-wrap justify-between gap-4',
        actionsAlign === 'start' ? 'items-start' : 'items-center',
        className,
      )}
    >
      {heading}
      {actions}
    </div>
  );
}
